import { useMemo, useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import { Clock, Search, Stethoscope } from 'lucide-react'

const DIFFICULTY_LABEL = { easy: 'Low', medium: 'Moderate', hard: 'High' }
const DIFFICULTY_STYLE = {
  easy: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  medium: 'border-amber-300 bg-amber-50 text-amber-700',
  hard: 'border-rose-300 bg-rose-50 text-rose-700',
}

const SPECIALTY_ORDER = [
  'Primary Care / General',
  'Cardiology',
  'Pulmonology',
  'Gastroenterology',
  'Endocrinology',
  'Nephrology',
  'Urology',
  'Neurology',
  'Psychiatry',
  'Hematology/Oncology',
  'Infectious Disease',
  'Rheumatology',
  'Orthopedics',
  'Dermatology',
  'ENT / Ophthalmology',
  'Obstetrics & Gynecology',
]

function formatMinutes(seconds) {
  if (!seconds || seconds <= 0) return '2 min'
  const m = Math.round(seconds / 60)
  return `${m} min`
}

function CaseCard({ kase }) {
  const difficulty = kase.difficulty || 'medium'
  return (
    <Link
      href={kase.url}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/80 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg hover:ring-2 hover:ring-sky-300"
    >
      <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-400 to-cyan-400" />
      <div className="flex items-start gap-3">
        {kase.avatarPreviewUrl ? (
          <img
            src={kase.avatarPreviewUrl}
            alt=""
            className="h-12 w-12 rounded-full object-cover ring-2 ring-white shadow-sm"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-cyan-400 text-white">
            <Stethoscope className="h-5 w-5" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold leading-tight text-slate-900">{kase.name}</h3>
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                DIFFICULTY_STYLE[difficulty] || DIFFICULTY_STYLE.medium
              }`}
            >
              {DIFFICULTY_LABEL[difficulty] || 'Moderate'}
            </span>
          </div>
          {kase.description && (
            <p className="mt-1 line-clamp-2 text-sm text-slate-600">{kase.description}</p>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          {formatMinutes(kase.timeLimitSeconds)}
        </span>
        <span className="font-semibold text-sky-600 group-hover:underline">Start case →</span>
      </div>
    </Link>
  )
}

function sortSpecialties(keys) {
  const known = SPECIALTY_ORDER.filter((k) => keys.includes(k))
  const extras = keys.filter((k) => !SPECIALTY_ORDER.includes(k)).sort()
  return [...known, ...extras]
}

export default function TrainingIndex({ cases = [] }) {
  const [query, setQuery] = useState('')
  const [activeSpecialty, setActiveSpecialty] = useState('All')

  const allSpecialties = useMemo(() => {
    const set = new Set(cases.map((c) => c.specialty || 'Other'))
    return sortSpecialties([...set])
  }, [cases])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return cases.filter((c) => {
      if (activeSpecialty !== 'All' && (c.specialty || 'Other') !== activeSpecialty) return false
      if (!q) return true
      const hay = `${c.name} ${c.description} ${c.specialty}`.toLowerCase()
      return hay.includes(q)
    })
  }, [cases, query, activeSpecialty])

  const grouped = useMemo(() => {
    const map = new Map()
    filtered.forEach((c) => {
      const key = c.specialty || 'Other'
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(c)
    })
    return sortSpecialties([...map.keys()]).map((k) => [k, map.get(k)])
  }, [filtered])

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      <Head title="Patient simulation training" />
      <header className="border-b border-white/60 bg-white/80 backdrop-blur sticky top-0 z-40">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <Link href="/" className="text-lg font-bold tracking-tight text-slate-900">
            ← Pop Health
          </Link>
          <span className="text-sm text-slate-500">Patient simulation</span>
        </div>
      </header>
      <main className="container mx-auto px-4 py-12">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Talk to a simulated patient
          </h1>
          <p className="mt-3 text-base text-slate-600">
            Pick a case. You have two minutes to interview the patient and submit a diagnosis.
            At the end you'll get feedback on communication, bedside manner, history taking, and
            diagnostic accuracy.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            {cases.length} cases across {allSpecialties.length} specialties.
          </p>
        </div>

        {cases.length === 0 ? (
          <div className="mx-auto mt-12 max-w-xl rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500">
            No training cases are set up yet. Ask an admin to seed some patient-sim agents.
          </div>
        ) : (
          <>
            <div className="mx-auto mt-10 flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, symptom, specialty…"
                  className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setActiveSpecialty('All')}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    activeSpecialty === 'All'
                      ? 'border-sky-500 bg-sky-500 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-sky-300'
                  }`}
                >
                  All
                </button>
                {allSpecialties.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setActiveSpecialty(s)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      activeSpecialty === s
                        ? 'border-sky-500 bg-sky-500 text-white'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-sky-300'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="mx-auto mt-12 max-w-xl rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500">
                No cases match your search.
              </div>
            ) : (
              <div className="mx-auto mt-10 max-w-6xl space-y-10">
                {grouped.map(([specialty, items]) => (
                  <section key={specialty}>
                    <div className="mb-3 flex items-baseline justify-between">
                      <h2 className="text-lg font-semibold tracking-tight text-slate-800">
                        {specialty}
                      </h2>
                      <span className="text-xs text-slate-500">
                        {items.length} {items.length === 1 ? 'case' : 'cases'}
                      </span>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((kase) => (
                        <CaseCard key={kase.slug} kase={kase} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
